import React, { useEffect, useState } from "react";
import SimpleReactValidator from "simple-react-validator";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { PulseLoader } from "react-spinners";
import { connect } from "react-redux";
import { useGoogleLogin } from "@react-oauth/google";
import { isEmpty } from "lodash";

import { getBaseURL, mergeClasses } from "../../utils";
import { store } from "../../store";
import { updateConfigs } from "../../store/actions/action";
import CloseEye from "../../icons/CloseEye";
import OpenEye from "../../icons/OpenEye";
import GoogleIcon from "../../icons/GoogleIcon";
import { useLoginModal } from "../../Context/LoginModalContext";

const Login = ({ configs, carts, className, ...props }) => {
  const { loginModal, closeLoginModal } = useLoginModal();
  const push = useNavigate();
  const location = useLocation();
  const { isAuth } = configs;

  useEffect(() => {
    if (isAuth) push("/");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [value, setValue] = useState({
    email: "",
    password: "",
    remember: false,
  });

  const changeHandler = (e) => {
    const { name, value: v, type, checked } = e.target;
    setValue((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : v }));
  };

  const toggleShowPassword = () => setShowPassword((s) => !s);

  const [validator] = useState(
    new SimpleReactValidator({ className: "gplg-error" })
  );

  const handelAddToCart = async (token) => {
    const data = carts?.map((el) => ({ productid: el?.id, quantity: el?.qty }));
    await axios
      .post(
        "add-cart",
        { products: data },
        { headers: { Authorization: `Bearer ${token}` } }
      )
      .catch(() => {});
  };

  const submitForm = async (e) => {
    e.preventDefault();
    if (!validator.fieldValid("email") || !validator.fieldValid("password")) {
      validator.showMessages();
      setValue((prev) => ({ ...prev }));
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${getBaseURL()}v1/auth/login`, {
        email: value.email,
        password: value.password,
        logintype: "email",
      });

      if (!isEmpty(carts)) await handelAddToCart(res.data.token);

      store.dispatch(
        updateConfigs({
          isAuth: true,
          token: res.data.token,
          ...res.data.userdetails,
        })
      );

      if (location?.state?.pathname) push(location.state.pathname);
      else if (location.pathname === "/login") {
        carts.length > 0 ? push("/cart") : push("/");
      }
      if (loginModal) closeLoginModal();
      toast.success(res?.data?.message || "Welcome back!");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Couldn't sign in. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async (userInfo) => {
    setLoading(true);
    try {
      const res = await axios.post(`register-oauth-google`, {
        token: { data: userInfo },
      });
      if (!isEmpty(carts)) await handelAddToCart(res.data.token);
      store.dispatch(
        updateConfigs({
          isAuth: true,
          token: res.data.token,
          ...res.data.userdetails,
        })
      );
      toast.success("Logged in successfully.");
      if (location?.state?.pathname) push(location.state.pathname);
      else if (location.pathname === "/login") push("/");
      if (loginModal) closeLoginModal();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Google sign-in failed.");
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = useGoogleLogin({
    onSuccess: async ({ access_token }) => {
      try {
        const userInfo = await axios.get("https://www.googleapis.com/oauth2/v3/userinfo", {
          headers: { Authorization: `Bearer ${access_token}` },
        });
        if (userInfo?.status === 200) handleGoogleLogin(userInfo.data);
      } catch {
        toast.error("Couldn't fetch Google profile.");
      }
    },
    onError: () => toast.error("Google sign-in failed."),
  });

  return (
    <>
      <div className={mergeClasses("gplg-card", className)}>
        <div className="gplg-head">
          <div className="gplg-eyebrow">
            <span className="gplg-eyebrow-dot" />
            WELCOME BACK
          </div>
          <h2 className="gplg-title">
            Sign in to your <span className="gplg-title-accent">account</span>
          </h2>
          <p className="gplg-sub">
            Sign in to track orders, redeem vouchers and access your concierge.
          </p>
        </div>

        <form className="gplg-form" onSubmit={submitForm} noValidate>
          <div className="gplg-field">
            <label htmlFor="gplg-email">Email Address</label>
            <div className="gplg-input-wrap">
              <span className="gplg-input-icon">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5z" /></svg>
              </span>
              <input
                id="gplg-email"
                type="email"
                value={value.email}
                name="email"
                onChange={changeHandler}
                onBlur={changeHandler}
                disabled={loading}
                placeholder="you@example.com"
                autoComplete="email"
              />
            </div>
            {validator.message("email", value.email, "required|email")}
          </div>

          <div className="gplg-field">
            <label htmlFor="gplg-password">Password</label>
            <div className="gplg-input-wrap">
              <span className="gplg-input-icon">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
              </span>
              <input
                id="gplg-password"
                type={showPassword ? "text" : "password"}
                value={value.password}
                name="password"
                onChange={changeHandler}
                onBlur={changeHandler}
                disabled={loading}
                placeholder="Your password"
                autoComplete="current-password"
              />
              <button
                type="button"
                className="gplg-eye-btn"
                onClick={toggleShowPassword}
                disabled={loading}
                aria-label={showPassword ? "Hide password" : "Show password"}
                tabIndex={-1}
              >
                {showPassword ? <CloseEye /> : <OpenEye />}
              </button>
            </div>
            {validator.message("password", value.password, "required")}
          </div>

          <div className="gplg-row">
            <label className="gplg-remember">
              <input
                type="checkbox"
                name="remember"
                checked={value.remember}
                onChange={changeHandler}
                disabled={loading}
              />
              <span className="gplg-remember-box">
                <svg viewBox="0 0 24 24" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </span>
              Remember me
            </label>
            <Link to={loading ? "#" : "/forgot"} className="gplg-link-forgot">
              Forgot password?
            </Link>
          </div>

          <button type="submit" className="gplg-submit" disabled={loading}>
            {loading ? (
              <PulseLoader size={8} color={"#0b0b0c"} />
            ) : (
              <>
                Sign In
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="12 5 19 12 12 19" />
                </svg>
              </>
            )}
          </button>
        </form>

        <div className="gplg-divider">
          <span>or continue with</span>
        </div>

        <button
          type="button"
          onClick={googleLogin}
          className="gplg-google"
          disabled={loading}
        >
          <GoogleIcon />
          <span>Sign in with Google</span>
        </button>

        <div className="gplg-foot">
          Don't have an account?{" "}
          <Link to={loading ? "#" : "/register"} className="gplg-link-strong">
            Create one
          </Link>
        </div>
      </div>

      <style>{`
        .gplg-card {
          width: 100%;
          max-width: 460px;
          background: linear-gradient(180deg, #15110a 0%, #1a1208 100%);
          border: 1px solid rgba(212, 168, 87, 0.25);
          border-radius: 20px;
          padding: 36px 32px 30px;
          box-shadow: 0 20px 50px rgba(0, 0, 0, 0.45);
          color: #e6e8ec;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
          margin: 0 auto;
        }

        /* HEAD */
        .gplg-head { text-align: center; margin-bottom: 24px; }
        .gplg-eyebrow {
          display: inline-flex; align-items: center; gap: 8px;
          padding: 5px 12px;
          background: rgba(212, 168, 87, 0.1);
          border: 1px solid rgba(212, 168, 87, 0.3);
          color: #d4a857;
          border-radius: 999px;
          font-size: 10.5px;
          font-weight: 700;
          letter-spacing: 1.8px;
          margin-bottom: 12px;
        }
        .gplg-eyebrow-dot {
          width: 5px; height: 5px;
          border-radius: 50%;
          background: #d4a857;
          box-shadow: 0 0 8px #d4a857;
          animation: gplg-pulse 1.6s ease-in-out infinite;
        }
        @keyframes gplg-pulse { 0%,100% { opacity:1; transform: scale(1); } 50% { opacity:.55; transform: scale(.85); } }
        .gplg-title {
          font-family: "Playfair Display", "Times New Roman", serif;
          font-size: 26px;
          font-weight: 700;
          color: #fff;
          margin: 0 0 8px;
          line-height: 1.25;
        }
        .gplg-title-accent {
          background: linear-gradient(135deg, #f1cf85, #b58a3f);
          -webkit-background-clip: text; background-clip: text; color: transparent;
          font-style: italic; font-weight: 600;
        }
        .gplg-sub {
          font-size: 13px;
          color: #9aa0a8;
          line-height: 1.55;
          margin: 0;
        }

        /* FORM */
        .gplg-form { display: flex; flex-direction: column; gap: 14px; margin-bottom: 16px; }
        .gplg-field { display: flex; flex-direction: column; gap: 6px; }
        .gplg-field label {
          font-size: 11px;
          color: #9aa0a8;
          font-weight: 700;
          letter-spacing: 1.2px;
          text-transform: uppercase;
        }
        .gplg-input-wrap {
          position: relative;
          display: flex; align-items: center;
        }
        .gplg-input-icon {
          position: absolute;
          left: 14px; top: 50%;
          transform: translateY(-50%);
          color: #d4a857;
          opacity: 0.7;
          pointer-events: none;
        }
        .gplg-input-wrap input {
          width: 100%;
          background: rgba(0, 0, 0, 0.35);
          border: 1px solid rgba(212, 168, 87, 0.22);
          border-radius: 10px;
          color: #fff;
          font-size: 14px;
          padding: 13px 44px 13px 38px;
          outline: none;
          font-family: inherit;
          transition: border-color .2s ease, background .2s ease;
        }
        .gplg-input-wrap input::placeholder { color: #6e7480; }
        .gplg-input-wrap input:focus {
          border-color: rgba(212, 168, 87, 0.6);
          background: rgba(0, 0, 0, 0.5);
        }
        .gplg-input-wrap input:focus + .gplg-eye-btn { color: #d4a857; }
        .gplg-input-wrap input:disabled { opacity: 0.6; cursor: not-allowed; }
        .gplg-eye-btn {
          position: absolute;
          right: 8px; top: 50%;
          transform: translateY(-50%);
          background: transparent;
          border: none;
          color: #9aa0a8;
          width: 32px; height: 32px;
          display: flex; align-items: center; justify-content: center;
          cursor: pointer;
          border-radius: 6px;
          font-family: inherit;
        }
        .gplg-eye-btn:hover { color: #d4a857; background: rgba(212, 168, 87, 0.08); }
        .gplg-eye-btn:disabled { opacity: 0.5; cursor: not-allowed; }
        .gplg-eye-btn svg { width: 16px; height: 16px; fill: currentColor; }

        .gplg-error {
          display: block;
          color: #ff7a7a;
          font-size: 12px;
          font-weight: 600;
          margin-top: 2px;
        }

        /* ROW */
        .gplg-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
          margin-top: -2px;
        }
        .gplg-remember {
          display: inline-flex; align-items: center; gap: 8px;
          font-size: 12.5px;
          color: #c5c8cf;
          cursor: pointer;
          user-select: none;
        }
        .gplg-remember input {
          position: absolute;
          opacity: 0;
          pointer-events: none;
        }
        .gplg-remember-box {
          width: 18px; height: 18px;
          background: rgba(0, 0, 0, 0.4);
          border: 1.5px solid rgba(212, 168, 87, 0.35);
          border-radius: 5px;
          display: inline-flex; align-items: center; justify-content: center;
          color: transparent;
          transition: background .2s ease, border-color .2s ease, color .2s ease;
          flex-shrink: 0;
        }
        .gplg-remember input:checked + .gplg-remember-box {
          background: linear-gradient(135deg, #f1cf85, #b58a3f);
          border-color: #d4a857;
          color: #0b0b0c;
        }
        .gplg-link-forgot {
          font-size: 12.5px;
          color: #d4a857;
          font-weight: 600;
          text-decoration: none;
          transition: color .2s ease;
        }
        .gplg-link-forgot:hover { color: #f1cf85; text-decoration: none; }

        /* SUBMIT */
        .gplg-submit {
          margin-top: 6px;
          width: 100%;
          display: inline-flex; align-items: center; justify-content: center; gap: 10px;
          padding: 14px;
          background: linear-gradient(135deg, #f1cf85, #d4a857 50%, #b58a3f);
          color: #0b0b0c;
          border: none;
          border-radius: 12px;
          font-size: 13px;
          font-weight: 800;
          letter-spacing: 0.6px;
          text-transform: uppercase;
          cursor: pointer;
          font-family: inherit;
          min-height: 50px;
          transition: transform .2s ease, box-shadow .25s ease, gap .2s ease;
        }
        .gplg-submit:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 14px 30px rgba(212, 168, 87, 0.35);
          gap: 12px;
        }
        .gplg-submit:disabled { opacity: 0.7; cursor: not-allowed; }

        /* DIVIDER */
        .gplg-divider {
          position: relative;
          text-align: center;
          margin: 22px 0 16px;
        }
        .gplg-divider::before {
          content: "";
          position: absolute;
          left: 0; right: 0; top: 50%;
          height: 1px;
          background: rgba(212, 168, 87, 0.18);
        }
        .gplg-divider span {
          position: relative;
          padding: 0 14px;
          background: linear-gradient(180deg, #15110a 0%, #1a1208 100%);
          font-size: 11px;
          font-weight: 700;
          color: #6e7480;
          letter-spacing: 1.4px;
          text-transform: uppercase;
        }

        /* GOOGLE */
        .gplg-google {
          width: 100%;
          display: inline-flex; align-items: center; justify-content: center; gap: 10px;
          padding: 13px;
          background: #fff;
          color: #1a1f2e;
          border: 1px solid rgba(255,255,255,0.85);
          border-radius: 12px;
          font-size: 13.5px;
          font-weight: 700;
          cursor: pointer;
          font-family: inherit;
          min-height: 48px;
          transition: transform .2s ease, box-shadow .25s ease, background .2s ease;
        }
        .gplg-google:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 12px 28px rgba(255, 255, 255, 0.12);
          background: #f8f9fa;
        }
        .gplg-google:disabled { opacity: 0.7; cursor: not-allowed; }
        .gplg-google svg { width: 18px; height: 18px; }

        /* FOOT */
        .gplg-foot {
          margin-top: 22px;
          padding-top: 18px;
          border-top: 1px dashed rgba(212, 168, 87, 0.18);
          text-align: center;
          font-size: 13px;
          color: #9aa0a8;
        }
        .gplg-link-strong {
          color: #d4a857;
          font-weight: 700;
          text-decoration: none;
          transition: color .2s ease;
        }
        .gplg-link-strong:hover { color: #f1cf85; text-decoration: underline; }

        @media (max-width: 480px) {
          .gplg-card { padding: 28px 22px 24px; border-radius: 16px; }
          .gplg-title { font-size: 22px; }
        }
      `}</style>
    </>
  );
};

const mapStateToProps = (state) => ({
  carts: state.cartList.cart,
  configs: state.configs,
});

export default connect(mapStateToProps)(Login);
